// src/components/block/liveStreamManagment/SafariAdviceModal.tsx
//
// Régie ouverte sous Safari : une boîte d'information conseille Chrome (2026-11-03).
//
// Safari n'a pas `captureStream()` sur une balise vidéo (la régie passe par un
// repli canvas + Web Audio, plus lourd), gère moins bien les entrées audio des
// cartes son et s'est déjà figé en direct (Akinax, 22/10). Le 03/11, le passage
// d'une régie Safari à Chrome a rétabli le son d'un direct. Rien n'est bloqué :
// la boîte informe, une fois par session de navigateur, et peut être désactivée.

import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { Info, X } from 'lucide-react';

const DISMISSED_SESSION_KEY = 'kleon.regie.safariAdvice.shown';
const NEVER_AGAIN_KEY       = 'kleon.regie.safariAdvice.never';
const CHROME_DOWNLOAD_URL   = 'https://www.google.com/chrome/';

/** Vrai pour Safari lui-même (Mac, iPad, iPhone), pas pour Chrome, Firefox ou Edge. */
export function isSafariBrowser(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return /Safari\//.test(ua)
      && /Version\//.test(ua)
      && !/Chrome\/|Chromium\/|CriOS\/|FxiOS\/|EdgiOS\/|Edg\/|OPR\//.test(ua);
}

const readFlag = (storage: () => Storage, key: string): boolean => {
  try { return storage().getItem(key) === '1'; } catch { return false; }
};
const writeFlag = (storage: () => Storage, key: string): void => {
  try { storage().setItem(key, '1'); } catch { /* stockage indisponible : tant pis */ }
};

const SafariAdviceModal: React.FC = () => {
  const [open, setOpen] = useState<boolean>(() =>
    isSafariBrowser()
    && !readFlag(() => window.localStorage, NEVER_AGAIN_KEY)
    && !readFlag(() => window.sessionStorage, DISMISSED_SESSION_KEY)
  );
  const [neverAgain, setNeverAgain] = useState(false);

  if (!open) return null;

  const close = () => {
    writeFlag(() => window.sessionStorage, DISMISSED_SESSION_KEY);
    if (neverAgain) writeFlag(() => window.localStorage, NEVER_AGAIN_KEY);
    setOpen(false);
  };

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="safari-advice-title"
      onClick={close}
    >
      <div
        className="relative w-full max-w-md rounded-xl border border-sky-500/50 bg-slate-900 p-6 text-slate-100 shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={close}
          aria-label="Fermer"
          className="absolute right-3 top-3 rounded-full p-1 text-slate-400 hover:bg-white/10 hover:text-white"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="mb-3 flex items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sky-500/20">
            <Info className="h-5 w-5 text-sky-300" />
          </span>
          <h2 id="safari-advice-title" className="text-lg font-semibold">
            Chrome conseillé pour le direct
          </h2>
        </div>

        <p className="mb-3 text-sm leading-relaxed text-slate-200">
          Pour une meilleure expérience du direct, nous vous conseillons d'utiliser la régie
          avec <span className="font-semibold">Google Chrome</span>.
        </p>
        <p className="mb-5 text-sm leading-relaxed text-slate-400">
          Sous Safari, certaines fonctions sont moins fiables : capture du son et de l'image
          des vidéos, sources audio des cartes son, tenue sur les longs directs. Vous pouvez
          continuer avec Safari, rien n'est bloqué.
        </p>

        <label className="mb-5 flex cursor-pointer items-center gap-2 text-sm text-slate-300">
          <input
            type="checkbox"
            checked={neverAgain}
            onChange={e => setNeverAgain(e.target.checked)}
            className="h-4 w-4 accent-sky-500"
          />
          Ne plus afficher ce message
        </label>

        <div className="flex flex-wrap justify-end gap-3">
          <a
            href={CHROME_DOWNLOAD_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-lg border border-slate-600 px-4 py-2 text-sm font-medium text-slate-200 hover:bg-white/10"
          >
            Télécharger Chrome
          </a>
          <button
            onClick={close}
            className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-500"
          >
            Continuer avec Safari
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default SafariAdviceModal;
